import React, { Component } from "react";
import {
  getSingleOrderApiRedux,
  getSingleProductRequestRedux,
} from "../../actions/index";
import { connect } from "react-redux";
import "./invoice-by-order.css";
import PaicartLogo from "../common/paicart_logo.png";
import { withRouter } from "react-router-dom";
import { getSingleUser } from "../../firebase/firebase.utils";
import {
  getTotalPaid,
  getTotalDue,
  getGoodsTotal,
  getSingleShopTotal,
} from "../../constants/orderMath";
import InvoiceShell, {
  InvoiceRow,
  InvoiceDivider,
  InvoiceSection,
  InvoiceBadge,
  InvoiceField,
  InvoiceGrid,
  InvoiceChip,
  InvoiceChipRow,
  InvoiceCartonTable,
  toInvoiceList,
} from "../common/invoiceShell.jsx";

const fmtDate = (ms) =>
  ms
    ? new Date(ms).toLocaleDateString("en-BD", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";

const tk = (amount) => `৳${parseInt(amount || 0).toLocaleString()}`;

const paymentBadgeColor = (status) =>
  status === "Paid" ? "emerald" : status === "Partially Paid" ? "orange" : "gray";

const paymentBadgeLabel = (status) =>
  status === "Paid"
    ? "Paid"
    : status === "Partially Paid"
      ? "Partially Paid"
      : "Not Paid";

/* Tracking numbers arrive as a scalar on some records and an array on
   others; "0" is the placeholder written before a real number exists. */
const formatTrackingNo = (value) =>
  toInvoiceList(value)
    .filter((t) => t !== "0")
    .join(", ");

/* Carton numbers here are relabelled CB -> AR for the admin copy. */
const toCartonList = (value) =>
  toInvoiceList(value).map((carton) => carton.replace("CB", "AR"));

/* Cartons and shipping marks are written one entry per carton, so equal-length
   lists pair up positionally. As grid cells these two wrapped into an
   unreadable block, hence their own full-width section. */
const CartonsSection = ({ record }) => {
  const cartons = toCartonList(record.ctnNo);
  const marks = toInvoiceList(record.shippingMark);
  if (cartons.length === 0 && marks.length === 0) return null;
  const paired = cartons.length > 0 && cartons.length === marks.length;
  return (
    <InvoiceSection title={paired ? "Cartons & Shipping Marks" : "Cartons"}>
      {paired ? (
        <InvoiceCartonTable cartons={cartons} marks={marks} />
      ) : (
        <>
          {cartons.length > 0 && (
            <InvoiceChipRow label="Carton No" items={cartons} />
          )}
          {marks.length > 0 && (
            <InvoiceChipRow label="Shipping Mark" items={marks} />
          )}
        </>
      )}
    </InvoiceSection>
  );
};

/* Status badges + payment history are identical on both invoice kinds. */
const StatusBadges = ({ record, statusValue }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
    <InvoiceBadge color={paymentBadgeColor(record.paymentStatus)}>
      {paymentBadgeLabel(record.paymentStatus)}
    </InvoiceBadge>
    <InvoiceBadge color={statusValue === "Reject" ? "red" : "gray"}>
      {statusValue || "Pending"}
    </InvoiceBadge>
  </div>
);

const PaymentHistory = ({ payments }) =>
  payments && payments.length > 0 ? (
    <InvoiceSection title="Payment History">
      {payments.map((p, i) => (
        <InvoiceRow
          key={i}
          label={`${p.method || "Payment"} — ${p.date || "—"}`}
          value={`+${tk(p.amount)}`}
          tone="green"
        />
      ))}
    </InvoiceSection>
  ) : null;

/* Order invoice - items table + full payment breakdown. */
const OrderInvoiceBody = ({ order, address }) => {
  const totalPaid = getTotalPaid(order);
  const totalDue = getTotalDue(order);
  const goodsTotal = getGoodsTotal(order);
  const hasShipping = parseInt(order.shippingChargeCustomer) > 0;
  const totalBill = totalDue + totalPaid;

  const lineItems = [];
  (order.items || []).forEach((item) => {
    (item.skus || []).forEach((sku) => {
      lineItems.push({
        name: item.name,
        color: sku.color,
        size: sku.size,
        qty: parseInt(sku.totalQuantity || 0),
        unitPrice: parseInt(parseFloat(sku.price || 0)),
      });
    });
  });
  const totalQty = lineItems.reduce((a, l) => a + l.qty, 0);

  return (
    <>
      <StatusBadges record={order} statusValue={order.orderStatus} />

      {address && (
        <InvoiceSection title="Bill To">
          <p style={{ fontSize: 13, fontWeight: "bold", color: "#1f2937" }}>
            {address.name || "—"}
          </p>
          <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
            {[
              address.address,
              address.thana,
              address.district,
              address.division,
            ]
              .filter(Boolean)
              .join(", ")}
          </p>
          {address.mobileNo && (
            <p style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {address.mobileNo}
            </p>
          )}
        </InvoiceSection>
      )}

      <InvoiceSection title="Order Info">
        <InvoiceGrid>
          <InvoiceField label="Shop" value={order.shopName} />
          <InvoiceField
            label="Tracking No"
            value={formatTrackingNo(order.trackingNo)}
          />
          <InvoiceField
            label="Shipment Method"
            value={order.shipmentMethod || "Air"}
          />
          <InvoiceField label="Order Date" value={fmtDate(order.time)} />
          {/* Admin-only references the customer copy has no use for, but the
              warehouse needs on a printed invoice. */}
        </InvoiceGrid>
      </InvoiceSection>

      <CartonsSection record={order} />

      <InvoiceSection title="Items">
        <div>
          {lineItems.map((l, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "8px 0",
                borderTop: i === 0 ? "none" : "1px solid #f3f4f6",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: "bold",
                    color: "#1f2937",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {l.name}
                </p>
                {(l.color || l.size) && (
                  <p style={{ fontSize: 11, color: "#9ca3af" }}>
                    {[l.color, l.size].filter(Boolean).join(" / ")}
                  </p>
                )}
                <p style={{ fontSize: 11, color: "#9ca3af" }}>
                  Qty: {l.qty} × {tk(l.unitPrice)}
                </p>
              </div>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: "bold",
                  color: "#111827",
                  flexShrink: 0,
                }}
              >
                {tk(l.qty * l.unitPrice)}
              </span>
            </div>
          ))}
        </div>
      </InvoiceSection>

      <InvoiceSection title="Payment Breakdown">
        <InvoiceRow
          label={`Product Subtotal (${totalQty} pcs)`}
          value={tk(goodsTotal)}
        />
        <InvoiceRow
          label="China Local Delivery Charge"
          value={parseInt(order.localShipping) > 0 ? tk(order.localShipping) : "—"}
        />
        {hasShipping ? (
          <InvoiceRow
            bold
            label="Shipping Charge (CN → BD)"
            value={tk(order.shippingChargeCustomer)}
          />
        ) : (
          <InvoiceRow
            label="Shipping Charge (CN → BD)"
            value="Pending actual weight"
          />
        )}
        {parseInt(order.otherCost) > 0 && (
          <InvoiceRow
            label="Packaging & Other Cost"
            value={`+${tk(order.otherCost)}`}
          />
        )}
        {parseInt(order.siteDiscountAmount) > 0 && (
          <InvoiceRow
            label={`Site Discount (${order.siteDiscountPercent}%)`}
            value={`-${tk(Math.round(order.siteDiscountAmount))}`}
            tone="green"
          />
        )}
        {parseInt(order.couponAmount) > 0 && (
          <InvoiceRow
            label="Coupon"
            value={`-${tk(order.couponAmount)}`}
            tone="green"
          />
        )}
        {parseInt(order.discount) > 0 && (
          <InvoiceRow
            label="Discount (on shipping)"
            value={`-${tk(order.discount)}`}
            tone="green"
          />
        )}

        <InvoiceDivider />
        <InvoiceRow bold label="Total Bill" value={tk(totalBill)} />
        {totalPaid > 0 && (
          <InvoiceRow
            label="Advance Paid"
            value={`-${tk(totalPaid)}`}
            tone="green"
          />
        )}
        <InvoiceDivider />
        <InvoiceRow
          bold
          label={totalDue > 0 ? "Balance Due" : "Fully Paid"}
          value={tk(totalDue > 0 ? totalDue : totalPaid)}
          tone={totalDue > 0 ? "due" : "green"}
        />
      </InvoiceSection>

      <PaymentHistory payments={order.payments} />
    </>
  );
};

/* Product-request invoice - simpler single-item breakdown. */
const ProductRequestInvoiceBody = ({ booking }) => {
  const totalPaid = getTotalPaid(booking);
  const totalDue = getTotalDue(booking);
  const hasShipping = parseInt(booking.shippingChargeCustomer) > 0;
  const base = booking.actualTotal
    ? parseInt(booking.actualTotal)
    : getSingleShopTotal(booking);
  const grandTotal = totalDue + totalPaid;

  return (
    <>
      <StatusBadges
        record={booking}
        statusValue={booking.orderStatus || booking.status}
      />

      <InvoiceSection title="Request Details">
        <InvoiceGrid>
          <InvoiceField label="Product" value={booking.productType} />
          <InvoiceField
            label="Store"
            value={[booking.store, booking.country].filter(Boolean).join(" · ")}
          />
          <InvoiceField label="Quantity" value={booking.productQuantity} />
          <InvoiceField
            label="Tracking No"
            value={formatTrackingNo(booking.trackingNo)}
          />
          <InvoiceField
            label="Request Date"
            value={booking.date || fmtDate(booking.time)}
          />
        </InvoiceGrid>
      </InvoiceSection>

      <CartonsSection record={booking} />

      {(booking.productVariant || booking.note) && (
        <InvoiceSection title="Notes">
          {booking.productVariant && (
            <p style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.6 }}>
              {booking.productVariant}
            </p>
          )}
          {booking.note && (
            <p
              style={{
                fontSize: 13,
                color: "#4b5563",
                lineHeight: 1.6,
                marginTop: 4,
              }}
            >
              {booking.note}
            </p>
          )}
        </InvoiceSection>
      )}

      <InvoiceSection title="Payment Breakdown">
        <InvoiceRow
          label="Product Price"
          hint={hasShipping ? "(excluding shipping)" : ""}
          value={base > 0 ? tk(base) : "—"}
        />
        <InvoiceRow
          label="China Local Courier Charge"
          value={
            parseInt(booking.localShipping) > 0 ? tk(booking.localShipping) : "—"
          }
        />
        {hasShipping && (
          <>
            <InvoiceRow
              label="Total Weight"
              value={booking.receivedWeight ? `${booking.receivedWeight} kg` : "—"}
            />
            <InvoiceRow
              label="Shipment Method"
              value={
                (booking.shipmentMethod || "Air") === "Sea" ? "By Sea" : "By Air"
              }
            />
            <InvoiceRow
              label="Customs & Shipping Rate"
              value={booking.customerRate ? `৳${booking.customerRate}/kg` : "—"}
            />
            <InvoiceRow
              bold
              label="Shipping Charge (CN → BD)"
              value={tk(booking.shippingChargeCustomer)}
            />
          </>
        )}
        <InvoiceDivider />
        <InvoiceRow bold label="Grand Total" value={tk(grandTotal)} />
        {totalPaid > 0 && (
          <InvoiceRow
            label="Advance Paid"
            value={`-${tk(totalPaid)}`}
            tone="green"
          />
        )}
        <InvoiceDivider />
        <InvoiceRow
          bold
          label={totalDue > 0 ? "Balance Due" : "Fully Paid"}
          value={tk(totalDue > 0 ? totalDue : totalPaid)}
          tone={totalDue > 0 ? "due" : "green"}
        />
      </InvoiceSection>

      <PaymentHistory payments={booking.payments} />
    </>
  );
};

export class OnlyInvoieToPrint extends Component {
  state = {
    user: null,
    productRequest: null,
    loading: true,
  };

  componentDidMount = async () => {
    const query = new URLSearchParams(this.props.location.search);
    const productRequest = query.get("productRequest");
    if (productRequest) {
      await this.props.getSingleProductRequestRedux(
        this.props.match.params.bookingId,
      );
    } else {
      await this.props.getSingleOrderApiRedux(this.props.match.params.bookingId);
    }
    this.setState({ productRequest, loading: false });
  };

  componentWillReceiveProps = async (nextProps) => {
    const record = this.state.productRequest
      ? nextProps.productRequestObj
      : nextProps.orderObj;
    if (record && record.userId && !this.state.user) {
      const user = await getSingleUser(record.userId);
      this.setState({ user });
    }
  };

  /* The order's own delivery address is authoritative; fall back to the
     customer's default shipping address for older records that carry none. */
  getAddress = (record) => {
    if (record && record.deliveryAddress) return record.deliveryAddress;
    const { user } = this.state;
    if (user && user.addressBook && user.addressBook.length > 0) {
      return (
        user.addressBook.find((address) => address.defaultShipping) ||
        user.addressBook[0]
      );
    }
    return null;
  };

  render() {
    const { orderObj, productRequestObj } = this.props;
    const { productRequest, loading } = this.state;
    const record = productRequest ? productRequestObj : orderObj;

    return (
      <InvoiceShell
        logo={PaicartLogo}
        docType={productRequest ? "Product Request Invoice" : "Order Invoice"}
        docId={this.props.match.params.bookingId}
        docDate={fmtDate(record && record.time)}
        loading={loading}
        notFound={!record}
      >
        {record &&
          (productRequest ? (
            <ProductRequestInvoiceBody booking={record} />
          ) : (
            <OrderInvoiceBody order={record} address={this.getAddress(record)} />
          ))}
      </InvoiceShell>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    orderObj: state.orders.orderObj,
    productRequestObj: state.productRequests
      ? state.productRequests.productRequestObj
      : null,
    admin: state.admins.currentAdmin,
    currency: state.users.currency,
  };
};

export default withRouter(
  connect(mapStateToProps, {
    getSingleOrderApiRedux,
    getSingleProductRequestRedux,
  })(OnlyInvoieToPrint),
);
